import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import { useGscConnection } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useGscConnection";
import { allTrackers } from "app/services/track/track";

import { getGscErrorMessage, isGscReconnectRequiredError } from "../gscProblemErrors";
import { useGscPropertiesQuery } from "../hooks";
import type {
    FixedGscConfig,
    GscSite,
    GscStateChangePayload,
    GscSyncInput,
    SavedGscMarket,
    StandardGscDraft,
} from "../types";
import { computeGscSyncStatus, findBestMatchingSite } from "../utils";

import { AccountPicker } from "./AccountPicker";
import { isGscCountrySupported } from "./GoogleMarket/utils";
import { GoogleMarketList } from "./GoogleMarketList";
import { Container } from "./styles";
import { buildSavedMarketsMap, hasGscSyncStatusErrors } from "./utils";
import { WebsiteMismatchAlert } from "./WebsiteMismatchAlert";
import { WebsitePicker } from "./WebsitePicker";

type Props = {
    // Fixed configuration from the standard wizard flow
    fixedConfig?: FixedGscConfig;
    // Standard-flow-only reopen draft. It restores previously saved market data but does not
    // replace the Step 1-2 constraints already encoded in `fixedConfig`.
    initialDraft?: StandardGscDraft | null;
    onStateChange: (payload: GscStateChangePayload) => void;
};

/**
 * Orchestrates the GSC keyword import flow.
 *
 * Renders a progressive sequence: Account -> Website property -> Market configurations.
 * Switching account resets the selected site to avoid stale property references.
 *
 * Markets are managed through a save/add/delete cycle:
 * - Saved markets render in a compact summary view
 * - At most one unsaved market form is shown at a time (controlled by `isAddingMarket`)
 * - "Add another Google market" appears after all current markets are saved
 *
 * On Google markets change (save/remove), this component emits the latest draft payload + validity status
 * to its consumer via `onStateChange`.
 */
const ImportFromGSC = ({ fixedConfig, initialDraft, onStateChange }: Props) => {
    const { translate } = useTranslate();
    const { locationsAndLanguagesLists, isLoadingLanguagesAndLocations } = useKeywordsCampaignsContext();

    const {
        connections = [],
        currentConnection,
        setCurrentConnection,
        connect,
        isConnecting,
        isLoadingConnections,
    } = useGscConnection();

    const isGscWizardFlow = !fixedConfig;

    const connectionId = currentConnection?.connectionId ?? null;

    // connectionId ?? 0 is a placeholder for the query key; the query is disabled when null, so 0 is never sent
    const { data: properties, error: propertiesError } = useGscPropertiesQuery(
        { connectionId: connectionId ?? 0 },
        { enabled: connectionId !== null },
    );

    const hasPropertiesFetchError = propertiesError != null;
    const needsReconnect = isGscReconnectRequiredError(propertiesError);

    // In standard flow, resolve website from wizard state to the actual GSC property
    // so backend APIs receive a valid format (full URLs or prefixed with "sc-domain:").
    const resolvedFixedSite = useMemo(() => {
        if (!fixedConfig?.website.domain || !properties) {
            return undefined;
        }
        return findBestMatchingSite(properties, fixedConfig.website.domain, fixedConfig.website.site);
    }, [fixedConfig?.website.domain, fixedConfig?.website.site, properties]);

    const [userSelectedSite, setUserSelectedSite] = useState<GscSite | undefined>();
    const selectedSite = userSelectedSite ?? resolvedFixedSite;

    // The standard wizard still derives the selected site from the fixed Step 1-2 config.
    // `initialDraft` only restores the saved market data that were submitted from this modal before.
    const initialMarketsMap = buildSavedMarketsMap(initialDraft);
    // Map of saved markets keyed by config index (fixedConfig flow) or auto-incrementing number (GSC-only).
    // Using a Map keeps insertion order and makes it easy to add/remove/lookup individual markets.
    const [savedMarkets, setSavedMarkets] = useState<Map<number, SavedGscMarket>>(initialMarketsMap);
    // Reopen in the compact saved-data state when a standard-flow draft exists; otherwise show
    // the first editable market form as usual.
    const [isAddingMarket, setIsAddingMarket] = useState(initialMarketsMap.size === 0);
    // Tracks which saved markets are currently in edit mode, so we can block "Finish" until all edits are committed.
    const [editingSavedMarketKeys, setEditingSavedMarketKeys] = useState<Set<number>>(new Set());

    const hasWebsiteMismatch = useMemo(() => {
        if (!fixedConfig || !properties || connectionId === null) {
            return false;
        }
        return !properties.some((p) => p.domain === fixedConfig.website.domain);
    }, [fixedConfig, properties, connectionId]);

    const filteredFixedProperties = useMemo(() => {
        if (!fixedConfig || !properties) {
            return properties;
        }
        return properties.filter((p) => p.domain === fixedConfig.website.domain);
    }, [fixedConfig, properties]);

    // Disable when in standard flow and there is zero or one matched website property
    const isWebsitePickerDisabled = !!fixedConfig && (filteredFixedProperties ?? []).length <= 1;
    const hasEmptyProperties = properties !== undefined && properties.length === 0;

    const languagesList = useMemo(
        () => locationsAndLanguagesLists?.languagesList ?? [],
        [locationsAndLanguagesLists?.languagesList],
    );
    const locationsList = useMemo(
        () => locationsAndLanguagesLists?.locationsList ?? [],
        [locationsAndLanguagesLists?.locationsList],
    );

    const hasUnsupportedSavedMarket = useMemo(() => {
        if (isLoadingLanguagesAndLocations || !locationsList.length) {
            return false;
        }
        return [...savedMarkets.values()].some(
            (market) => !isGscCountrySupported(market.countryCode, locationsList),
        );
    }, [savedMarkets, locationsList, isLoadingLanguagesAndLocations]);

    // Emit the latest local draft state and validity to the consumer.
    useEffect(() => {
        const input: GscSyncInput = {
            selectedSite,
            savedMarkets,
            hasUnsavedMarket: isAddingMarket || editingSavedMarketKeys.size > 0,
        };
        const status = computeGscSyncStatus(input);
        onStateChange({
            input,
            status,
            isValid:
                !hasGscSyncStatusErrors(status) &&
                !hasWebsiteMismatch &&
                !hasPropertiesFetchError &&
                !isLoadingLanguagesAndLocations &&
                !hasUnsupportedSavedMarket,
        });
    }, [
        selectedSite,
        savedMarkets,
        isAddingMarket,
        editingSavedMarketKeys,
        onStateChange,
        hasWebsiteMismatch,
        hasPropertiesFetchError,
        isLoadingLanguagesAndLocations,
        hasUnsupportedSavedMarket,
    ]);

    const handleSaveMarket = useCallback((market: SavedGscMarket, configIndex: number) => {
        setSavedMarkets((prev) => new Map(prev).set(configIndex, market));
        setIsAddingMarket(false);
    }, []);

    const handleRemoveMarket = useCallback(
        (configIndex: number) => {
            setSavedMarkets((prev) => {
                const next = new Map(prev);
                next.delete(configIndex);
                return next;
            });
            setEditingSavedMarketKeys((prev) => {
                const next = new Set(prev);
                next.delete(configIndex);
                return next;
            });
            if (isGscWizardFlow) {
                allTrackers.dispatchTrackEvent("Drop Down", "click", "GSC Wizard/Website market deleted");
            }
        },
        [isGscWizardFlow],
    );

    const handleStartAdding = useCallback(() => {
        setIsAddingMarket(true);
        if (isGscWizardFlow) {
            allTrackers.dispatchTrackEvent("Button", "click", "GSC Wizard/Additional market");
        }
    }, [isGscWizardFlow]);
    const handleCancelAdding = useCallback(() => setIsAddingMarket(false), []);

    const handleSavedMarketEditingChange = useCallback((configIndex: number, isEditing: boolean) => {
        setEditingSavedMarketKeys((prev) => {
            if (isEditing === prev.has(configIndex)) {
                return prev;
            }
            const next = new Set(prev);
            if (isEditing) {
                next.add(configIndex);
            } else {
                next.delete(configIndex);
            }
            return next;
        });
    }, []);

    // For the fixed configs, display the settings when we have properties, and they match the website.
    const showSettings =
        connectionId !== null &&
        !hasPropertiesFetchError &&
        !hasWebsiteMismatch &&
        (!fixedConfig || properties !== undefined);

    let accountErrorMessage: string | undefined;
    if (hasPropertiesFetchError) {
        accountErrorMessage = getGscErrorMessage(propertiesError, translate);
    } else if (hasWebsiteMismatch) {
        accountErrorMessage = translate("rankTracker.gsc.accountPicker.websiteMismatchError");
    } else if (hasEmptyProperties) {
        accountErrorMessage = translate("rankTracker.gsc.accountPicker.noPropertiesError");
    }

    return (
        <Container>
            {hasWebsiteMismatch && fixedConfig ? <WebsiteMismatchAlert domain={fixedConfig.website.domain} /> : null}
            <AccountPicker
                connections={connections}
                currentConnection={currentConnection}
                requireConfirmation={!!selectedSite}
                onSelect={(connection) => {
                    setCurrentConnection(connection.ownerEmail);
                    setUserSelectedSite(undefined);
                    setSavedMarkets(new Map());
                    setEditingSavedMarketKeys(new Set());
                    setIsAddingMarket(true);
                    if (isGscWizardFlow) {
                        allTrackers.dispatchTrackEvent("Drop Down", "click", "GSC Wizard/Account selected", {
                            availableAccounts: connections.length ?? 0,
                        });
                    }
                }}
                onConnectNewAccount={connect}
                isLoading={isConnecting || isLoadingConnections}
                hasError={hasPropertiesFetchError || hasWebsiteMismatch || hasEmptyProperties}
                errorMessage={accountErrorMessage}
                onReconnect={needsReconnect ? connect : undefined}
            />
            {showSettings ? (
                <>
                    <WebsitePicker
                        isDisabled={isWebsitePickerDisabled}
                        properties={filteredFixedProperties}
                        selectedSite={selectedSite}
                        requireConfirmation={!!selectedSite}
                        onSelect={(site) => {
                            setUserSelectedSite(site);
                            setSavedMarkets(new Map());
                            setEditingSavedMarketKeys(new Set());
                            setIsAddingMarket(true);
                            if (isGscWizardFlow) {
                                allTrackers.dispatchTrackEvent("Drop Down", "click", "GSC Wizard/Website selected", {
                                    availableWebsites: properties?.length ?? 0,
                                });
                            }
                        }}
                    />
                    {selectedSite ? (
                        <GoogleMarketList
                            connectionId={connectionId}
                            site={selectedSite.site}
                            languagesList={languagesList}
                            locationsList={locationsList}
                            isLocationsLoading={isLoadingLanguagesAndLocations}
                            fixedConfig={fixedConfig}
                            savedMarkets={savedMarkets}
                            isAddingMarket={isAddingMarket}
                            onSaveMarket={handleSaveMarket}
                            onRemoveMarket={handleRemoveMarket}
                            onStartAdding={handleStartAdding}
                            onCancelAdding={handleCancelAdding}
                            onSavedMarketEditingChange={handleSavedMarketEditingChange}
                        />
                    ) : null}
                </>
            ) : null}
        </Container>
    );
};

export default ImportFromGSC;
